import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';

interface Props {
  title: string;
  /** Trail such as "Catalog / Industry Configs" — rendered as uppercase crumbs. */
  breadcrumb?: string;
  description?: string;
  right?: React.ReactNode;
  backTo?: string;
  /** Custom back action (e.g. to confirm unsaved changes); shows the arrow like backTo. */
  onBack?: () => void;
}

// Same header as slightercrm/src/components/Topbar.tsx: sticky, blurred,
// uppercase crumbs, a semibold display title and an optional one-line summary.
export const Topbar: React.FC<Props> = ({ title, breadcrumb, description, right, backTo, onBack }) => {
  const nav = useNavigate();
  const crumbs = breadcrumb ? breadcrumb.split('/').map((c) => c.trim()).filter(Boolean) : [];
  return (
    <header className="sticky top-0 z-30 border-b border-border/80 bg-surface/90 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:gap-6 sm:px-6 sm:py-4">
        <div className="flex min-w-0 items-center gap-3">
          {(backTo || onBack) && (
            <button
              type="button"
              onClick={() => (onBack ? onBack() : nav(backTo!))}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
              aria-label="Back"
              title="Back"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="min-w-0">
            {crumbs.length > 0 && (
              <nav aria-label="Breadcrumb" className="flex items-center gap-1 overflow-hidden whitespace-nowrap text-2xs font-medium uppercase tracking-[0.14em] text-text-muted">
                {crumbs.map((c, i) => (
                  <span key={`${c}-${i}`} className="flex items-center gap-1">
                    {i > 0 && <ChevronRight size={10} className="opacity-60" aria-hidden />}
                    <span>{c}</span>
                  </span>
                ))}
              </nav>
            )}
            <h1 className="mt-0.5 truncate font-display text-[18px] font-semibold leading-tight tracking-tight text-text-primary sm:text-[20px]">
              {title}
            </h1>
            {description && (
              <p className="mt-0.5 truncate text-[12px] text-text-muted sm:text-[12.5px]">{description}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {right}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};
